// Local history service - stores conversation history in localStorage
// This is used as a fallback for unauthenticated users

export interface LocalConversation {
  id: string;
  title: string;
  prompt: string;
  timestamp: Date;
}

const STORAGE_KEY = 'pinak_local_history';
const MAX_HISTORY_ITEMS = 50;

/**
 * Get all local conversations from localStorage
 */
export function getLocalHistory(): LocalConversation[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const parsed = JSON.parse(stored);
    return parsed.map((item: any) => ({
      ...item,
      timestamp: new Date(item.timestamp),
    }));
  } catch (error) {
    console.error('Error reading local history:', error);
    return [];
  }
}

/**
 * Add a conversation to local history
 */
export function addToLocalHistory(prompt: string, title?: string): LocalConversation {
  const history = getLocalHistory();
  
  // Generate a title from the prompt if not provided
  const generatedTitle = title || generateTitle(prompt);
  
  const newConversation: LocalConversation = {
    id: 'local_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
    title: generatedTitle,
    prompt: prompt,
    timestamp: new Date(),
  };
  
  // Add to beginning of array
  history.unshift(newConversation);
  
  // Limit history size
  const trimmedHistory = history.slice(0, MAX_HISTORY_ITEMS);
  
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedHistory));
  } catch (error) {
    console.error('Error saving local history:', error);
  }
  
  return newConversation;
}

/**
 * Update a conversation title in local history
 */
export function updateLocalHistoryTitle(id: string, title: string): void {
  const history = getLocalHistory();
  const updated = history.map(item => 
    item.id === id ? { ...item, title } : item
  );
  
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error('Error updating local history:', error);
  }
}

/**
 * Remove a conversation from local history
 */
export function removeFromLocalHistory(id: string): void {
  const history = getLocalHistory();
  const filtered = history.filter(item => item.id !== id);
  
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Error removing from local history:', error);
  }
}

/**
 * Clear all local history
 */
export function clearLocalHistory(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing local history:', error);
  }
}

/**
 * Generate a title from a prompt
 */
function generateTitle(prompt: string): string {
  // Take first 40 chars and clean up
  let title = prompt.trim().slice(0, 40);
  
  // If we cut in the middle of a word, go back to the last space
  if (prompt.length > 40) {
    const lastSpace = title.lastIndexOf(' ');
    if (lastSpace > 20) {
      title = title.slice(0, lastSpace);
    }
    title += '...';
  }
  
  // Capitalize first letter
  return title.charAt(0).toUpperCase() + title.slice(1);
}
